import React, { useState } from 'react'

import { Popover, PopoverProps } from '@mui/material'

import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Icon from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'

import NumberField from 'components_new/molecules/NumberField'

interface EditSegmentPopoverProps {
  anchorPosition: PopoverProps['anchorPosition']
  onClose: () => void
  onSubmit: (value: number) => void
  open: PopoverProps['open']
  title?: string
  value: {
    edited: number | null
    original: number
  }
}

const EditSegmentPopover = (props: EditSegmentPopoverProps) => {
  const { anchorPosition, onClose, onSubmit, open, title, value } = props

  const { t } = useTranslation()
  const startValue = (value.edited ?? value.original)
    .toString()
    .replace('.', ',')

  const [editValue, setEditValue] = useState<string>(startValue)

  const handleSubmit = () => {
    onSubmit(parseFloat(editValue.replace(',', '.')))
    onClose()
  }

  const handleCancel = () => {
    setEditValue(startValue)
    onClose()
  }

  const handleOnChange = (value: string) => {
    setEditValue(value)
  }

  return (
    <Popover
      anchorPosition={anchorPosition}
      anchorReference="anchorPosition"
      onClose={onClose}
      open={open}
    >
      <Box sx={{ p: 2, pb: 1 }}>
        {title ? (
          <Text fontWeight="bold" variant="body1" sx={{ mb: 1 }}>
            {title}
          </Text>
        ) : null}

        <Box
          sx={{
            alignItems: 'center',
            bgcolor: 'background.default',
            borderRadius: 1,
            display: 'flex',
            gap: 2,
            mb: 1,
            p: 1
          }}
        >
          <Box>
            <Text
              color="text.secondary"
              component="div"
              noWrap
              variant="caption"
            >
              {t('global', 'original_value')}
            </Text>
            <Text
              color={value.edited ? 'text.disabled' : undefined}
              component="div"
              noWrap
              sx={{ fontVariantNumeric: 'tabular-nums' }}
              variant="navigation"
            >
              {/*This value can not be formatted or use ValueText 
              for transparency */}
              {value.original}
            </Text>
          </Box>
          <Icon color="disabled" name="ArrowForward" sx={{ fontSize: '1em' }} />
          <Box>
            <Text
              color="text.secondary"
              component="div"
              noWrap
              variant="caption"
            >
              {t('global', 'edited_value')}
            </Text>
            <Text
              color={value.edited === null ? 'text.disabled' : undefined}
              component="div"
              noWrap
              sx={{ fontVariantNumeric: 'tabular-nums' }}
              variant="navigation"
            >
              {/*This value can not be formatted or use ValueText 
              for transparency */}
              {value.edited}
            </Text>
          </Box>
        </Box>

        <NumberField
          onChange={handleOnChange}
          size="small"
          type="decimal"
          value={editValue}
        />
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Button onClick={handleCancel} variant="text">
            {t('global', 'cancel')}
          </Button>
          <Button onClick={handleSubmit}>{t('global', 'save')}</Button>
        </Box>
      </Box>
    </Popover>
  )
}

export default EditSegmentPopover
